import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Chat, Message, Pagination, RateMessageForm, SendMessageForm, SendMessageResponse, Tag } from '@/types'
import { type Form, useForm } from '@/composables/useForm.ts'
import axios, { AxiosError } from 'axios'
import { FormError } from '@/utils/FormError'
import { useTrackStore } from '@stores/track.ts'
import { useEventBus } from '@vueuse/core'
import router from '@/router'
import type { UserQuota } from '@/types/User.ts'
import type { AppMessage } from '@/types/Message.ts'
import { cacheKeys } from '@/enum/cacheKeys.ts'
import { scrollToLastUserMessage } from '@/composables/useChatScroll'
import { client as httpClient } from 'laravel-precognition-vue'
import { type ContentType, messageSender, streamMessageType } from '@/enum'
import type { StreamMessage } from '@/types/StreamMessage.ts'

export const useChatStore = defineStore('chat', () => {
  const chats = ref<Chat[]>([])
  const currentChat = ref<Chat | null>(null)
  const messages = ref<AppMessage[]>([])
  const trendingTags = ref<Tag[]>([])
  const loading = ref(false)
  const error = ref<FormError | null>(null)
  let abortController = new AbortController()
  const authStore = useAuthStore()
  const cacheStore = useCacheStore()
  const messageFinishedBus = useEventBus<AppMessage>('message-finished')
  const createChatForm = useForm<Record<string, never>, Chat>('post', '/chats')
  const sendMessageForm = useForm<SendMessageForm, SendMessageResponse>('post', () => {
    if (!currentChat.value) {
      throw new Error('No chat selected')
    }

    return currentChat.value ? `/chats/${currentChat.value.id}/messages` : ''
  },
  {
    media_id: null,
    text: '',
  })

  const rateMessageForm = (messageId: number) => useForm<RateMessageForm, Message>('put',
    () =>
      currentChat.value ? `/chats/${currentChat.value.id}/messages/${messageId}/rate` : '',
    {
      rate: null,
    }
  )

  const quotaForm = useForm<Record<string, unknown>, UserQuota>('get', '/auth/quota', {
    chat_id: null,
  })

  const fetchQuota = async () => {
    quotaForm.chat_id = currentChat.value?.id
    const { data } = await quotaForm.submit()
    quota.value = data
    return data
  }

  const quota = ref<UserQuota>({
    messages: {
      used: 0,
      total: 0,
      remaining: Infinity,
      reset_in: undefined,
    },
    uploads: {
      used: 0,
      total: 0,
      remaining: Infinity,
      reset_in: undefined,
    },
  })

  const updateQuota = (remainingMessages: number, remainingUploads: number) => {
    const currentRemainingMessages = quota.value.messages.remaining
    const currentRemainingUploads = quota.value.uploads.remaining

    if (currentRemainingMessages !== 0 && remainingMessages === 0) {
      quota.value.messages.reset_in = 60 * 60 * 24
    }

    if (currentRemainingUploads !== 0 && remainingUploads === 0) {
      quota.value.uploads.reset_in = 60 * 60 * 24
    }

    quota.value.messages.remaining = remainingMessages
    quota.value.uploads.remaining = remainingUploads
  }

  const hasReachedQuota = computed(() => {
    if (authStore.isSubscribed) {
      return false
    }

    if (!authStore.isAuthenticated) {
      return false
    }

    return quota.value.messages.remaining === 0
  })
  const hasReachedUploadQuota = computed(() => {
    if (authStore.isSubscribed) {
      return false
    }

    if (!authStore.isAuthenticated) {
      return false
    }

    return quota.value.uploads.remaining === 0
  })

  const hasChats = computed(() => chats.value.length > 0)

  const currentMessages = computed(() => messages.value)

  const fetchChats = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await useForm<Record<string, unknown>, Pagination<Chat>>('get', '/chats', {
        per_page: 100,
        sort: '-updated_at',
      }).submit()

      chats.value = response.data.data
      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to fetch chats')
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  const createChat = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await createChatForm.submit()
      chats.value.unshift(response.data)
      createChatForm.reset()
      setCurrentChat(response.data)
      authStore.managePersonalizationDisabling()
      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to create chat')
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  const fetchTrendingTags = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await useForm<Tag, Tag[]>('get', '/chats/trending-tags').submit()
      return trendingTags.value = response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to fetch trending tags')
      }
      throw e
    } finally {
      loading.value = false
    }
  }


  const closeChat = async (chatId: string) => {
    try {
      loading.value = true
      error.value = null
      await useForm('put', `/chats/${chatId}/close`).submit()
      const chatIndex = chats.value.findIndex(chat => chat.id === chatId)
      if (chatIndex !== -1) {
        chats.value[chatIndex] = { ...chats.value[chatIndex], closed_at: new Date().toISOString() }
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to close chat')
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateChatTitle = async (chatId: string, title: string | null) => {
    try {
      loading.value = true
      error.value = null
      await useForm('put', `/chats/${chatId}`, {
        title,
      }).submit()

    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to update chat title ')
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  const deleteChat = async (chatId: string) => {
    try {
      useTrackStore().track('delete_history_clicked')
      loading.value = true
      error.value = null
      await useForm('delete', `/chats/${chatId}`).submit()
      chats.value = chats.value.filter(chat => chat.id !== chatId)

      if (currentChat.value?.id == chatId) {
        await router.push('/')
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to delete chat')
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  const formatMessage = (message: Message): AppMessage => ({ ...message, typingState: 'finished' })

  const fetchMessages = async (chatId: string) => {
    try {
      loading.value = true
      error.value = null
      const response = await useForm<Record<string, unknown>, Pagination<Message>>('get', `/chats/${chatId}/messages`, { per_page: 100 }).submit()
      messages.value = response?.data?.data.map(formatMessage) ?? []

      return messages.value
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to fetch messages')
      }
      throw e
    } finally {
      loading.value = false
    }
  }


  const chatStream = async (
    onMessage: (payload: StreamMessage) => void,
    onCancel: () => void
  ) => {
    abortController?.abort()
    abortController = new AbortController()

    const response = await httpClient.axios().post(
      `/chats/${currentChat.value!.id}/messages/stream`,
      {
        text: sendMessageForm.text,
        media_id: sendMessageForm.media_id,
      },
      {
        responseType: 'stream',
        adapter: 'fetch',
        signal: abortController.signal,
      }
    )

    const stream = response.data as ReadableStream
    const reader = stream.getReader()
    const decoder = new TextDecoder('utf-8')


    let buffer = ''


    try {

      const processLine = (line: string) => {
        const cleanLine = line.trim()
        if (!cleanLine.startsWith('data: ')) return
        try {
          const jsonStr = cleanLine.replace(/^data: /, '')
          onMessage(JSON.parse(jsonStr) as StreamMessage)
        } catch (e) {
          console.error('Parsing error', e)
          console.error('line', cleanLine)
        }
      }


      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          processLine(line)
        }
      }
      if (buffer.trim()) {
        processLine(buffer)
      }
    } catch (error: unknown) {
      console.log('cancel error',error)
      if ((error instanceof Error && error.name === 'AbortError') || axios.isCancel(error)) {
        onCancel()
      }
    } finally {
      reader.releaseLock()
    }

    return response.headers
  }

  const sendMessage = async () => {
    if (!currentChat.value) {
      throw new Error('No chat selected')
    }

    try {
      loading.value = true
      sendMessageForm.processing = true
      error.value = null


      messages.value.push({
        id: 0,
        chat_id: currentChat.value.id,
        media_id: sendMessageForm.media_id ?? null,
        sender: messageSender.USER,
        text: sendMessageForm.text || '',
        rate: 0,
        is_guest: true,
        created_at: new Date().toString(),
        updated_at: new Date().toString(),
        chat: currentChat.value,
        typingState: 'finished',
      })

      const userMessageIndex = messages.value.length - 1
      const sinaMessageIndex = userMessageIndex + 1

      await scrollToLastUserMessage(messages.value)

      // let classifiedContentType: ContentType | undefined = undefined
      await chatStream(payload => {
        const { type, data } = payload

        switch (type) {
          case streamMessageType.CONTENT:
            messages.value[sinaMessageIndex].text += data.content
            messages.value[sinaMessageIndex].typingState = 'typing'
            messages.value[sinaMessageIndex].loadingState = 'typing'
            break
          case streamMessageType.SINA_MESSAGE:
            messages.value[activeMessageIndex.value!] = {
              id: data.id,
              chat_id: data.chat_id,
              media_id: null,
              sender: messageSender.AI,
              text: data.text || '',
              rate: 0,
              created_at: data.created_at,
              updated_at: data.updated_at,
              chat: currentChat.value!,
              data: data.data,
              typingState: 'finished',
              loadingState: 'finished',
            }
            nextTick(() => {
              messageFinishedBus.emit(messages.value[activeMessageIndex.value!])
            })
            if (data?.chat?.title && currentChat.value) {
              currentChat.value.title = data?.chat?.title
            }
            break
          case streamMessageType.SEARCH:
            messages.value[sinaMessageIndex].loadingState = 'searching'
            break
          case streamMessageType.EXTERNAL_SEARCH:
            if (data?.status === 'done') {
              messages.value[sinaMessageIndex].loadingMessage = undefined
              break
            }
            messages.value[sinaMessageIndex].loadingState = 'searching'
            messages.value[sinaMessageIndex].loadingMessage = data?.message as string|undefined
            break
          case streamMessageType.CLASSIFICATION:
            messages.value.push({
              id: 0,
              chat_id: currentChat.value!.id,
              media_id: sendMessageForm.media_id ?? null,
              sender: messageSender.AI,
              text: '',
              rate: 0,
              is_guest: true,
              created_at: new Date().toString(),
              updated_at: new Date().toString(),
              chat: currentChat.value!,
              typingState: 'typing',
              loadingState: 'classifying',
              data: {
                content_type: data.content_type as ContentType,
              },
            })
            activeMessageIndex.value = messages.value.length - 1
            if (sendMessageForm.processing) {
              sendMessageForm.processing = false
            }
            break
          case streamMessageType.FINISH:
            messages.value[activeMessageIndex.value!].loadingState = 'finished'
            messages.value[activeMessageIndex.value!].typingState = 'finished'
            break
          case streamMessageType.USER_MESSAGE:
            messages.value[userMessageIndex] = {
              id: data.id,
              chat_id: data.chat_id,
              media_id: data?.media?.id || null,
              sender: messageSender.USER,
              text: data.text || '',
              rate: 0,
              is_guest: false,
              created_at: data.created_at,
              updated_at: data.updated_at,
              chat: currentChat.value!,
              media: data.media,
              typingState: 'finished',
            }
            if (data?.chat?.title && currentChat.value) {
              currentChat.value.title = data?.chat?.title
            }
            break
          case streamMessageType.METADATA:
          case streamMessageType.OTHER_KEYS:
          case streamMessageType.STATUS:
            console.log('message ignored', type,data)
            break
          case streamMessageType.ERROR:
            console.error('message error', type,data)
            useTrackStore().track('error_message_sent',{
              chat_id:currentChat.value?.id,
              type,
              error: JSON.stringify(data),
            })
            if (messages.value[sinaMessageIndex]) {
              messages.value[sinaMessageIndex].typingState = 'finished'
              messages.value[sinaMessageIndex].loadingState = 'finished'
            }
            break
          default:
            useTrackStore().track('unknown_message_type_sent',{
              chat_id:currentChat.value?.id,
              type,
              error: JSON.stringify(data),
            })
            console.log('message type not found', type,data)
        }
      }, () => {
        console.log('message canceled!!!!!!')
        if (messages.value[sinaMessageIndex]) {
          messages.value[sinaMessageIndex].typingState = 'finished'
          messages.value[sinaMessageIndex].loadingState = 'finished'
        }

      })


      activeMessageIndex.value = messages.value.length - 1

      sendMessageForm.reset()
      sendMessageForm.media_id = null
      sendMessageForm.text = ''

      await fetchQuota()

      changeActiveToTop()

    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to send message')
      }

      sendMessageForm.reset()
      sendMessageForm.media_id = null
      sendMessageForm.text = ''
      throw e
    } finally {
      loading.value = false
      sendMessageForm.processing = false
    }
  }

  const changeActiveToTop = () => {
    const index = chats.value.findIndex(chat => chat.id === currentChat.value?.id)
    if (index <= 0) {
      return
    }
    const [itemToMove] = chats.value.splice(index, 1)
    chats.value.unshift(itemToMove)
  }

  const rateMessage = async (form: Form<RateMessageForm, Message>) => {
    if (!currentChat.value) {
      throw new Error('No chat selected')
    }

    try {
      error.value = null
      const response = await form.submit()

      for (const [key, message] of currentMessages.value.entries()) {
        if (message.id === response.data.id) {
          currentMessages.value[key].rate = response.data.rate
          break
        }
      }

      return response.data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to rate message')
      }
      throw e
    }
  }

  const setCurrentChat = (chat: Chat | null, reset = false) => {
    currentChat.value = chat
    if (reset) {
      sendMessageForm.reset()
    }
  }

  const getCurrentChat = async () => {
    if (!currentChat.value) {
      await createChat()
    }

    return currentChat.value
  }

  const clearError = () => {
    error.value = null
  }

  const getChatById = async (id: string) => {
    try {
      const { data } = await useForm<Record<string, unknown>, Chat>('get', `/chats/${id}`).submit()

      return data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed get chat')
      }

      throw e
    } finally {
      loading.value = false
    }
  }

  const $reset = async () => {
    currentChat.value = null
    messages.value = []
    trendingTags.value = []
    loading.value = false
    error.value = null
    createChatForm.reset()
    sendMessageForm.reset()
    activeMessageIndex.value = undefined
    quota.value = {
      messages: {
        used: 0,
        total: 0,
        remaining: Infinity,
        reset_in: undefined,
      },
      uploads: {
        used: 0,
        total: 0,
        remaining: Infinity,
        reset_in: undefined,
      },
    }
  }

  watch(currentChat, async () => {
    abortController?.abort()
    quota.value = await fetchQuota()
  })

  const getSavedStorageSession = () => {
    const sessionValue = cacheStore.get(cacheKeys.chatSession)
    if (!sessionValue) {
      return
    }

    removeSavedStorageSession()

    return sessionValue.value
  }

  const removeSavedStorageSession = () => {
    cacheStore.remove(cacheKeys.chatSession)
  }

  const saveSessionToStorage = () => {
    if (!currentChat.value) {
      return
    }

    cacheStore.set(cacheKeys.chatSession, currentChat.value.id)
  }

  // typing state
  const activeMessageIndex = ref<number | undefined>(undefined)

  const typingMessage = computed(() => {
    if (!activeMessageIndex.value) return null
    const msg = messages.value[activeMessageIndex.value]

    if (!msg || msg.typingState !== 'typing') {
      // activeMessageIndex.value = undefined
      return null
    }

    return msg
  })

  const isTyping = computed(() => !!typingMessage.value)


  const finishTyping = () => {
    abortController?.abort()
  }


  return {
    chats,
    currentChat,
    messages,
    trendingTags,
    loading,
    error,

    createChatForm,
    sendMessageForm,
    rateMessageForm,

    hasChats,
    currentMessages,
    quota,
    hasReachedQuota,
    hasReachedUploadQuota,

    fetchChats,
    getChatById,
    createChat,
    fetchTrendingTags,
    closeChat,
    updateChatTitle,
    deleteChat,
    fetchMessages,
    sendMessage,
    rateMessage,
    setCurrentChat,
    getCurrentChat,
    clearError,
    $reset,
    getSavedStorageSession,
    saveSessionToStorage,
    removeSavedStorageSession,

    // typing state
    isTyping,
    finishTyping,
    activeMessageIndex,
    typingMessage,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useChatStore, import.meta.hot))
}
